import { Menu, MessageCircle, Monitor, Moon, Sun, X, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ThemeSelector } from "../ui/themeSelector";
import { LanguageSelector } from "../ui/languageSelector";
import logo from '../../assets/icon.png'; // Adjust the path as necessary
import { useNavigate } from "react-router-dom";
import { useNavigateWithScroll } from "../../hooks/useNavigateWithScroll";


export const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const { t, i18n } = useTranslation();
  const navigate = useNavigateWithScroll()

  return (
    <nav id="top" className="fixed top-0 w-full bg-white/80 dark:bg-gray-900/80 backdrop-blur-md z-50 border-b border-gray-200 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <button onClick={()=>{navigate("/")}} className="flex items-center">
            {/* <MessageCircle className="w-8 h-8 text-purple-600" /> */}
            <img width={35} src={logo} alt="" />
            <span className="ml-2 text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              3wChat
            </span>
          </button>

          {/* Navigation Desktop */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => navigate('/')}
              className={`transition-colors ${activeSection === 'home' ? 'text-purple-600' : 'text-gray-700 dark:text-gray-300 hover:text-purple-600'}`}
            >
              {t('nav.home')}
            </button>
            
            {/* Menu Solutions Desktop */}
            <div className="relative">
              <button
                onClick={() => setIsSolutionsOpen(!isSolutionsOpen)}
                className="flex items-center space-x-1 text-gray-700 dark:text-gray-300 hover:text-purple-600 transition-colors"
              >
                <span>Solutions</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isSolutionsOpen ? 'rotate-180' : ''}`} />
              </button>
              
              <AnimatePresence>
                {isSolutionsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2"
                  >
                    <button
                      onClick={() => {
                        navigate('/individual');
                        setIsSolutionsOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-purple-600 transition-colors"
                    >
                      {t('nav.individual')}
                    </button>
                    <button
                      onClick={() => {
                        navigate('/business');
                        setIsSolutionsOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-purple-600 transition-colors"
                    >
                      {t('nav.business')}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button
              onClick={() => navigate('/features')}
              className={`transition-colors ${activeSection === 'features' ? 'text-purple-600' : 'text-gray-700 dark:text-gray-300 hover:text-purple-600'}`}
            >
              {t('nav.features')}
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className={`transition-colors ${activeSection === 'pricing' ? 'text-purple-600' : 'text-gray-700 dark:text-gray-300 hover:text-purple-600'}`}
            >
              {t('nav.pricing')}
            </button>
            <button
              onClick={() => navigate('/contact')}
              className={`transition-colors ${activeSection === 'contact' ? 'text-purple-600' : 'text-gray-700 dark:text-gray-300 hover:text-purple-600'}`}
            >
              {t('nav.contact')}
            </button>
          </div>

          {/* Actions Desktop */}
          <div className="hidden md:flex items-center space-x-4">
            {/* <LanguageSelector /> */}
            {/* <ThemeSelector /> */}
            <button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all">
              {t('nav.login')}
            </button>
          </div>

          {/* Menu Mobile */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg text-gray-600 dark:text-gray-400"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Menu Mobile Ouvert */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-gray-200 dark:border-gray-700"
            >
              <div className="py-4 space-y-4">
                <button
                  onClick={() => {
                    navigate('/');
                    setIsOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300"
                >
                  {t('nav.home')}
                </button>
                
                {/* Menu Solutions Mobile */}
                <div>
                  <button
                    onClick={() => setIsSolutionsOpen(!isSolutionsOpen)}
                    className="flex items-center justify-between w-full px-4 py-2 text-gray-700 dark:text-gray-300"
                  >
                    <span>Solutions</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isSolutionsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  <AnimatePresence>
                    {isSolutionsOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="pl-8 space-y-2"
                      >
                        <button
                          onClick={() => {
                            navigate('/individual');
                            setIsOpen(false);
                            setIsSolutionsOpen(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-gray-600 dark:text-gray-400"
                        >
                          {t('nav.individual')}
                        </button>
                        <button
                          onClick={() => {
                            navigate('/business');
                            setIsOpen(false);
                            setIsSolutionsOpen(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-gray-600 dark:text-gray-400"
                        >
                          {t('nav.business')}
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <button
                  onClick={() => {
                    navigate('/features');
                    setIsOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300"
                >
                  {t('nav.features')}
                </button>
                <button
                  onClick={() => {
                    navigate('/pricing');
                    setIsOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300"
                >
                  {t('nav.pricing')}
                </button>
                <button
                  onClick={() => {
                    navigate('/contact');
                    setIsOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300"
                >
                  {t('nav.contact')}
                </button>
                <div className="flex items-center justify-between px-4 py-2">
                  {/* <LanguageSelector /> */}
                  <ThemeSelector />
                </div>
                <div className="px-4">
                  <button className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg">
                    {t('nav.login')}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};